import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { subscriptionKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";

import type {
  CardPaymentMethod,
  PaymentMethodAction,
  PaymentMethods,
} from "../types";

import { applyPaymentMethodAction } from "../utils/payment-method-slots";

export function usePaymentMethods(
  projectId: number,
): UseQueryResult<PaymentMethods> {
  const { data: userData } = useUser();

  return useQuery<PaymentMethods>({
    enabled: !!userData,
    queryFn: () => {
      const project = userData!.projects.find((p) => p.id === projectId);

      if (!project) {
        throw new Error("Project not found");
      }

      return buildMockPaymentMethods();
    },
    queryKey: subscriptionKeys.paymentMethods(projectId),
    staleTime: Infinity,
  });
}

export function usePaymentMethodMutation(
  projectId: number,
): UseMutationResult<PaymentMethods, Error, PaymentMethodAction> {
  const queryClient = useQueryClient();
  const queryKey = subscriptionKeys.paymentMethods(projectId);

  return useMutation({
    mutationFn: async (action: PaymentMethodAction) => {
      const current = queryClient.getQueryData<PaymentMethods>(queryKey);

      if (!current) {
        throw new Error("Payment methods are not loaded yet.");
      }

      return applyPaymentMethodAction(current, action);
    },
    onSuccess: (paymentMethods) => {
      queryClient.setQueryData(queryKey, paymentMethods);
    },
  });
}

export const MOCK_DEFAULT_CARD: CardPaymentMethod = {
  brand: "visa",
  expiryMonth: 12,
  expiryYear: 2028,
  id: "pm_mock_default",
  last4: "4242",
  type: "card",
};

function buildMockPaymentMethods(): PaymentMethods {
  return {
    backupMethods: [],
    defaultMethod: MOCK_DEFAULT_CARD,
  };
}
